import { type FC, useEffect } from 'react';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useApiAdminlocalesPost } from '@r/api/generated/client.ts';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

interface AdminLocaleFormProps {
  isUserSuperAdmin: boolean;
  onSuccess: () => void;
}

export const AdminLocaleForm: FC<AdminLocaleFormProps> = ({ onSuccess, isUserSuperAdmin }) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();

  const newLocaleMutation = useApiAdminlocalesPost({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.translations.new.form.success'));
        onSuccess();
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('admin.translations.new.form.error'));
      },
      networkMode: 'always'
    }
  });

  const newLocaleFormSchema = z.object({
    label: z.string().min(1, {
      message: t('admin.translations.new.form.label.error')
    }),
    content: z.string().min(1, {
      message: t('admin.translations.new.form.content.error')
    })
  });

  const newLocaleForm = useForm<z.infer<typeof newLocaleFormSchema>>({
    resolver: zodResolver(newLocaleFormSchema),
    defaultValues: {
      label: '',
      content: ''
    }
  });

  const handlenewLocale = (data: z.infer<typeof newLocaleFormSchema>) => {
    newLocaleMutation.mutate({
      data: {
        label: data.label,
        content: data.content,
        country: '/countries/FRA',
        isPublic: isUserSuperAdmin,
        isEditable: true
      }
    });
  };
  useEffect(() => {
    if (newLocaleMutation.isSuccess) {
      newLocaleForm.reset();
    }
  }, [newLocaleMutation.isSuccess, newLocaleForm]);

  return (
    <Form {...newLocaleForm}>
      <form onSubmit={newLocaleForm.handleSubmit(handlenewLocale)}>
        <div className="flex flex-col gap-4">
          <FormField
            control={newLocaleForm.control}
            name="label"
            render={({ field, fieldState }) => (
              <FormItem className="flex-1">
                <FormLabel>{t('admin.translations.new.form.label')}</FormLabel>
                <div className="flex gap-4 items-center">
                  <Input
                    disabled
                    placeholder={t('admin.translations.new.form.label.placeholder')}
                  />
                  <FormControl className="flex-1">
                    <Input
                      placeholder={t('admin.translations.new.form.label.example')}
                      {...field}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={newLocaleForm.control}
            name="content"
            render={({ field, fieldState }) => (
              <FormItem className="flex-1">
                <FormLabel>{t('admin.translations.new.form.content')}</FormLabel>
                <div className="flex gap-4 items-center">
                  <Input
                    disabled
                    placeholder={t('admin.translations.new.form.content.placeholder')}
                  />
                  <FormControl className="flex-1">
                    <Input
                      placeholder={t('admin.translations.new.form.content.example')}
                      {...field}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button loading={newLocaleMutation.isPending} className="mt-5 w-full" type="submit">
          {t('admin.translations.new.form.button')}
        </Button>
      </form>
    </Form>
  );
};
